<template>
    <div class="enrollment-wizard">
        <div class="wizard-container" :class="{'wizard-mobile': mobile}">
            <!-- Left Sidebar with Steps -->
            <wizard-sidebar 
                :current-step="currentStep" 
                :steps="steps" 
                :mobile="mobile" 
            />

            <!-- Right Content Area -->
            <div class="wizard-content">
                <div class="content-header">
                    <span class="step-counter">{{ currentStep }}/{{ steps.length }}</span>
                    <h3 class="content-title">{{ stepTitles[currentStep - 1] }}</h3>
                    <p class="content-subtitle">{{ stepSubtitles[currentStep - 1] }}</p>
                </div>

                <v-form class="wizard-form">
                    <!-- Step 1: Basic Info -->
                    <wizard-step-basic-info 
                        v-show="currentStep === 1"
                        :date-of-birth.sync="formData.dateOfBirth"
                        :preferred-days.sync="formData.preferredDays"
                        :max-date-of-birth="maxDateOfBirth"
                    />

                    <!-- Step 2: Evaluation Class -->
                    <wizard-step-evaluation 
                        v-show="currentStep === 2"
                        :evaluation-date.sync="formData.evaluationDate"
                        :evaluation-time.sync="formData.evaluationTime"
                        :min-evaluation-date="minEvaluationDate"
                        :mobile="mobile"
                    />

                    <!-- Step 3: Trial Class -->
                    <wizard-step-trial-class 
                        v-show="currentStep === 3"
                        :trial-date.sync="formData.trialDate"
                        :trial-time.sync="formData.trialTime"
                        :min-trial-date="minTrialDate"
                        :mobile="mobile"
                    />

                    <!-- Step 4: Weekly Schedule -->
                    <wizard-step-schedule 
                        v-show="currentStep === 4"
                        :class-duration.sync="formData.classDuration"
                        :classes-per-week.sync="formData.classesPerWeek"
                        :start-date.sync="formData.startDate"
                        :time-entries.sync="formData.timeEntries"
                        :preferred-days="formData.preferredDays"
                        :course-duration-items="courseDurationItems"
                        :classes-per-week-items="classesPerWeekItems"
                        :min-start-date="minStartDate"
                        :mobile="mobile"
                    />

                    <!-- Step 5: Final Details -->
                    <wizard-step-final 
                        v-show="currentStep === 5"
                        :comment.sync="formData.comment"
                    />
                </v-form>

                <!-- Navigation Buttons -->
                <div class="wizard-actions">
                    <span v-if="currentStep > 1" class="back-link" @click="previousStep">Back</span>
                    <div v-else></div>
                    
                    <button v-if="currentStep < steps.length" class="primary-btn" @click="nextStep">
                        Next Step
                    </button>
                    <button v-else class="primary-btn" :disabled="enrolling" @click="handleEnroll">
                        <span v-if="enrolling">Loading...</span>
                        <span v-else>Enroll Now</span>
                    </button>
                </div>

                <v-alert v-if="showHelp" type="error" dense text class="medium-text mt-4" icon="mdi-lifebuoy" color="primary">
                    <span class="strong-text--text">
                        Contact <router-link :to="{name: 'help'}"><b class="primary--text text-decoration-underline">Support</b></router-link> for help.
                    </span>
                </v-alert>
            </div>
        </div>
    </div>
</template>

<script>
import WizardSidebar from './WizardSidebar.vue';
import WizardStepBasicInfo from './WizardStepBasicInfo.vue';
import WizardStepEvaluation from './WizardStepEvaluation.vue';
import WizardStepTrialClass from './WizardStepTrialClass.vue';
import WizardStepSchedule from './WizardStepSchedule.vue';
import WizardStepFinal from './WizardStepFinal.vue';

export default {
    name: 'EnrollmentWizard',
    components: {
        WizardSidebar,
        WizardStepBasicInfo,
        WizardStepEvaluation,
        WizardStepTrialClass,
        WizardStepSchedule,
        WizardStepFinal
    },
    props: {
        course: {
            type: Object,
            required: true
        },
        courseMeta: {
            type: Object,
            default: null
        },
        mobile: {
            type: Boolean,
            default: false
        },
        enrolling: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const d = new Date();
        d.setDate(d.getDate() + 1); // min start_date is tomorrow

        const trialMin = new Date();

        const evaluationMin = new Date();

        const max = new Date();
        max.setFullYear(max.getFullYear() - 3);

        return {
            currentStep: 1,
            showHelp: false,
            minStartDate: d.toISOString().substr(0, 10),
            minTrialDate: trialMin.toISOString().substr(0, 10),
            minEvaluationDate: evaluationMin.toISOString().substr(0, 10),
            maxDateOfBirth: max.toISOString().substr(0, 10),
            formData: {
                dateOfBirth: '',
                preferredDays: [],
                evaluationDate: '',
                evaluationTime: '',
                trialDate: '',
                trialTime: '',
                classDuration: '',
                classesPerWeek: '',
                startDate: '',
                comment: '',
                timeEntries: [
                    { day: '', from: '', to: '' },
                    { day: '', from: '', to: '' },
                    { day: '', from: '', to: '' },
                ]
            },
            steps: [
                { label: 'Basic Info', key: 'basic' },
                { label: 'Evaluation Class', key: 'evaluation' },
                { label: 'Trial Class', key: 'trial' },
                { label: 'Weekly Schedule', key: 'schedule' },
                { label: 'Final Details', key: 'final' }
            ]
        };
    },
    computed: {
        stepTitles() {
            return [
                'Your Basic Information',
                'Book Your Evaluation Class',
                'Book Your Free Trial',
                'Set Your Weekly Availability',
                'Final Touches'
            ];
        },
        stepSubtitles() {
            return [
                'Let us know a few things to personalize your course experience',
                'Choose a suitable date and time for your 15-minute evaluation session',
                'Choose a suitable date and time for your free trial session',
                "Tell us how many classes you want and when you're available",
                'Add any notes or preferences before we confirm your enrollment'
            ];
        },
        courseDurationItems() {
            if (!this.courseMeta) {
                return [];
            }
            const items = this.courseMeta.class_duration_items;
            return items.map(item => {
                const hourlyPrice = this.course.enrollment && this.course.enrollment.hourly_price 
                    ? this.course.enrollment.hourly_price 
                    : this.course.hourly_price;
                const classPrice = hourlyPrice * (item.value / 60);
                return {
                    text: item.text,
                    value: item.value,
                };
            });
        },
        classesPerWeekItems() {
            let items = [
                { text: '2 classes / week', value: 2 },
                { text: '3 classes / week', value: 3 },
                { text: '4 classes / week', value: 4 },
                { text: '5 classes / week', value: 5 },
                { text: '6 classes / week', value: 6 },
            ];
            // if one class duration not less than 1 hour, add one class to items
            if (this.formData.classDuration && this.formData.classDuration / 60 >= 1) {
                items.unshift({ text: '1 class / week', value: 1 });
            }
            return items;
        },
        enrollmentData() {
            return {
                class_duration: this.formData.classDuration,
                weekly_classes: this.formData.classesPerWeek,
                start_day: this.formData.startDate,
                suggested_evaluation_day: this.formData.evaluationDate,
                evaluation_time: this.formData.evaluationTime,
                suggested_trial_day: this.formData.trialDate,
                trial_time: this.formData.trialTime,
                date_of_birth: this.formData.dateOfBirth,
                comment: this.formData.comment,
                preferred_days: this.formData.preferredDays,
                schedule: this.formData.timeEntries.filter(item => !!item.day || item.day === 0)
            };
        }
    },
    methods: {
        nextStep() {
            if (this.validateStep(this.currentStep)) {
                if (this.currentStep < this.steps.length) {
                    this.currentStep++;
                }
            }
        },
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        validateStep(step) {
            switch (step) {
                case 1:
                    if (!this.formData.dateOfBirth) {
                        this.$emit('show-message', { text: 'Date of Birth is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.preferredDays || this.formData.preferredDays.length === 0) {
                        this.$emit('show-message', { text: 'Please select at least one preferred day', type: 'error' });
                        return false;
                    }
                    return true;
                case 2:
                    if (!this.formData.evaluationDate) {
                        this.$emit('show-message', { text: 'Evaluation Date is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.evaluationTime) {
                        this.$emit('show-message', { text: 'Evaluation Time is required', type: 'error' });
                        return false;
                    }
                    return true;
                case 3:
                    if (!this.formData.trialDate) {
                        this.$emit('show-message', { text: 'Trial Date is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.trialTime) {
                        this.$emit('show-message', { text: 'Trial Time is required', type: 'error' });
                        return false;
                    }
                    return true;
                case 4: {
                    if (!this.formData.startDate) {
                        this.$emit('show-message', { text: 'Start Date is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.classDuration) {
                        this.$emit('show-message', { text: 'Class Duration is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.classesPerWeek) {
                        this.$emit('show-message', { text: 'Classes per Week is required', type: 'error' });
                        return false;
                    }
                    if (this.formData.preferredDays.length < this.formData.classesPerWeek) {
                        this.$emit('show-message', { text: '"Preferred Days" must be equal or more than "Classes/ Week"', type: 'error' });
                        this.showHelp = true;
                        return false;
                    }
                    const scheduleEntries = this.formData.timeEntries.filter(item => !!item.day || item.day === 0);
                    if (scheduleEntries.length < this.formData.classesPerWeek) {
                        this.$emit('show-message', { text: '"Schedule" times must be equal or more than "Classes/ Week"', type: 'error' });
                        this.showHelp = true;
                        return false;
                    }
                    let timeValid = true;
                    scheduleEntries.forEach(scheduleItem => {
                        if (!scheduleItem.from) {
                            this.$emit('show-message', { text: 'Time input is required for all schedule entries', type: 'error' });
                            this.showHelp = true;
                            timeValid = false;
                        }
                    });
                    if (!timeValid) return false;
                    return true;
                }
                case 5:
                    return true;
                default:
                    return true;
            }
        },
        validate() {
            const data = this.enrollmentData;
            if (!data.class_duration || !data.weekly_classes || !data.start_day || !data.evaluation_time || !data.trial_time || !data.date_of_birth) {
                this.$emit('show-message', { text: 'Required inputs cannot be empty', type: 'error' });
                return false;
            } else if (data.preferred_days.length < data.weekly_classes) {
                this.$emit('show-message', { text: '"Preferred Days" must be equal or more than "Classes/ Week"', type: 'error' });
                this.showHelp = true;
                return false;
            } else if (data.schedule.length < data.weekly_classes) {
                this.$emit('show-message', { text: '"Schedule" times must be equal or more than "Classes/ Week"', type: 'error' });
                this.showHelp = true;
                return false;
            }

            let timeValid = true;
            data.schedule.forEach(scheduleItem => {
                if (!scheduleItem.from) {
                    this.$emit('show-message', { text: 'Time input is required', type: 'error' });
                    this.showHelp = true;
                    timeValid = false;
                }
            });
            if (!timeValid) return false;
            return true;
        },
        handleEnroll() {
            // Validate all steps before submitting
            let allStepsValid = true;
            for (let i = 1; i <= this.steps.length; i++) {
                if (!this.validateStep(i)) {
                    allStepsValid = false;
                    this.currentStep = i;
                    break;
                }
            }

            if (allStepsValid && this.validate()) {
                this.$emit('enroll', this.enrollmentData);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.enrollment-wizard {
    background-color: #FAFAFA;
    border-radius: 8px;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
}

.wizard-container {
    display: flex;
    background: #FFFFFF;
    border-radius: 8px;
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.12);
    min-height: 500px;

    &.wizard-mobile {
        flex-direction: column;
    }
}

.wizard-content {
    flex: 1;
    padding: 48px;
    display: flex;
    flex-direction: column;
}

.content-header {
    margin-bottom: 40px;
}

.step-counter {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.21em;
    color: rgba(30, 30, 30, 0.48);
    display: block;
    margin-bottom: 20px;
}

.content-title {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 24px;
    line-height: 1.21em;
    color: #1E1E1E;
    margin-bottom: 12px;
}

.content-subtitle {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.21em;
    color: rgba(30, 30, 30, 0.64);
    margin: 0;
}

.wizard-form {
    flex: 1;
}

.wizard-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 32px;
    gap: 16px;
}

.back-link {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #1E1E1E;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.7;
    }
}

.primary-btn {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #FFFFFF;
    background-color: #E79007;
    border: none;
    border-radius: 6px;
    padding: 10px 24px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #d18306;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

// Ensure form inputs use Inter font
.wizard-form {
    ::v-deep .v-text-field,
    ::v-deep .v-select,
    ::v-deep .v-textarea {
        font-family: 'Inter', sans-serif;
    }

    ::v-deep .v-label {
        font-family: 'Inter', sans-serif;
    }

    ::v-deep .v-input__slot {
        font-family: 'Inter', sans-serif;
    }
}

@media (max-width: 960px) {
    .enrollment-wizard {
        border-radius: 12px;
    }

    .wizard-container {
        border-radius: 12px;
    }

    .wizard-content {
        padding: 20px 16px;
    }

    .content-header {
        margin-bottom: 24px;
    }

    .step-counter {
        font-size: 14px;
        margin-bottom: 12px;
    }

    .content-title {
        font-size: 18px;
        margin-bottom: 8px;
    }

    .content-subtitle {
        font-size: 14px;
    }

    .wizard-actions {
        flex-direction: column-reverse;
        gap: 12px;
        align-items: stretch;
        margin-top: 24px;
    }

    .primary-btn {
        width: 100%;
        font-size: 14px;
        padding: 12px 20px;
    }

    .back-link {
        font-size: 14px;
        text-align: center;
        padding: 8px 0;
    }
}
</style>

